import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Button, Message } from '@getro/rombo';
import { Mail } from 'lucide-react';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import { SocialOAuthButtons } from 'components/molecules/socialOAuthButtons';
import { openChat } from 'helpers/supportChat';
import { legalDocumentsVersion } from 'settings/legalDocumentsVersion';
import { logOut } from 'actions/userActions';
import { useClaim } from '../hooks/useClaim';

export const SignupAuth = ({ domain, company, activeNetwork, isOnboarding }) => {
  const [oAuthError, setOAuthError] = useState(null);
  const history = useHistory();
  const { url } = useRouteMatch();
  const { claimOrganization, isClaiming } = useClaim();

  return (
    <>
      <Flex sx={{ flexDirection: 'column', gap: '16px' }}>
        <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
          <SocialOAuthButtons
            isLoading={isClaiming}
            params={
              !isOnboarding
                ? {
                    collectionId: activeNetwork?.id,
                    claimOrgProfile: company?.id,
                    privacyPolicyVersion: legalDocumentsVersion.privacyPolicyVersion,
                    termsPolicyVersion: legalDocumentsVersion.termsPolicyVersion,
                    onboardingCompleted: true,
                    role: 'company_admin',
                  }
                : {}
            }
            mode={isOnboarding ? 'sign-in' : 'sign-up'}
            isInvite={isOnboarding}
            disableLinkedIn
            onSuccess={async (userData) => {
              const [, companyDomain] = userData.email.split('@');
              if (domain !== companyDomain && !isOnboarding) {
                setOAuthError({
                  status: 422,
                  data: { error: 'You need to use an email with the company domain.' },
                });

                logOut();
                return;
              }

              try {
                await claimOrganization({ activeNetwork, company, userData, isOnboarding });
              } catch (error) {
                setOAuthError({
                  status: 500,
                  data: { error: 'Failed to claim organization. Please try again.' },
                });
              }
            }}
            onError={(e) => {
              setOAuthError(e);
            }}
          />
          <Button
            mt="8px"
            icon={Mail}
            variant="secondary"
            width="100%"
            sx={{ gap: '16px', justifyContent: 'center' }}
            onClick={() => history.push(`${url}/profile`)}
          >
            Continue with email
          </Button>
          {!!oAuthError && (
            <Message
              mt="8px"
              type="error"
              content={
                <>
                  {oAuthError?.status === 401 ? oAuthError.data?.error : ''}
                  {oAuthError?.status === 422 && (
                    <Text>
                      You need to use an email with the company domain. If you still think you should be able to claim
                      this profile,{' '}
                      <Text as="span" sx={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={openChat}>
                        contact us
                      </Text>
                      .
                    </Text>
                  )}
                </>
              }
            />
          )}
        </Flex>

        <Text sx={{ color: 'text.main', fontSize: '14px', textAlign: 'center' }}>Use your @{domain} email.</Text>
      </Flex>
      <Text sx={{ color: 'text.main', fontSize: '14px', textAlign: 'center' }}>
        Already have an account?
        <Box as={Link} to="/login" sx={{ color: 'text.main', fontSize: '14px', textAlign: 'center' }}>
          Log in.
        </Box>
      </Text>
    </>
  );
};

SignupAuth.propTypes = {
  domain: PropTypes.string.isRequired,
  company: PropTypes.object.isRequired,
  activeNetwork: PropTypes.object.isRequired,
  isOnboarding: PropTypes.bool.isRequired,
};
